import React, { useEffect, useState } from 'react';
import { Form, Input, DatePicker, Button, Select, Row, Col, Alert } from 'antd';
import CBInputNumber from '../../../components/CBInputNumber';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { enregistrerCheque, listerFacturesParCommande, listerBanques } from '../../../services/attestationsService';
import CBModal from '../../../components/CBModal';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../../i18n/format';
const { Option } = Select;

/**
 * Props :
 *  - open / onClose
 *  - commandeId : si fourni, charge auto les factures de la commande
 *  - facturePreselectionnee : { factureTrackingId, numeroFacture, montantTotal } (court-circuite le select)
 *  - onCreated(cheque)
 */
export default function EnregistrerChequeModal({ open, onClose, onCreated, commandeId, facturePreselectionnee }) {
  const { t } = useTranslation('attestations');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [factures, setFactures] = useState([]);
  const [factureSel, setFactureSel] = useState(null);
  const [banques, setBanques] = useState([]);

  useEffect(() => {
    if (!open) return;
    listerBanques().then(setBanques).catch(() => setBanques([]));
    if (facturePreselectionnee) {
      setFactures([facturePreselectionnee]);
      setFactureSel(facturePreselectionnee);
      form.setFieldsValue({
        factureTrackingId: facturePreselectionnee.factureTrackingId,
        montant: facturePreselectionnee.montantTotal,
      });
    } else if (commandeId) {
      listerFacturesParCommande(commandeId)
        .then((list) => {
          const visibles = list.filter(f => !f.annulee);
          setFactures(visibles);
          if (visibles.length === 1) {
            setFactureSel(visibles[0]);
            form.setFieldsValue({
              factureTrackingId: visibles[0].factureTrackingId,
              montant: visibles[0].montantTotal,
            });
          }
        })
        .catch(() => setFactures([]));
    }
  }, [open, commandeId, facturePreselectionnee, form]);

  const onFinish = async (vals) => {
    setSubmitting(true);
    try {
      const cheque = await enregistrerCheque({
        factureTrackingId: vals.factureTrackingId,
        numeroCheque: vals.numeroCheque?.trim(),
        montant: vals.montant,
        banqueEmettrice: vals.banqueEmettrice || null,
        dateEmission: vals.dateEmission?.format('YYYY-MM-DD'),
        dateReception: vals.dateReception?.format('YYYY-MM-DD') || null,
      });
      toast.success(t('modales.cheque.enregistre', { numero: cheque?.numeroCheque }));
      form.resetFields();
      setFactureSel(null);
      onCreated?.(cheque);
      onClose?.();
    } catch (_) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CBModal title={t('modales.cheque.titre')}
      open={open}
      onCancel={() => { form.resetFields(); setFactureSel(null); onClose?.(); }}
      footer={null}
      width={620}
      destroyOnClose>
      <Form form={form} layout="vertical" onFinish={onFinish}
        initialValues={{ dateEmission: dayjs(), dateReception: dayjs() }}>

        <Form.Item name="factureTrackingId" label={t('modales.cheque.factureConcernee')} rules={[{ required: true }]}>
          <Select showSearch placeholder={factures.length === 0 ? t('modales.cheque.aucuneFactureEmise') : t('modales.cheque.selectionnerFacture')}
            disabled={!!facturePreselectionnee || factures.length === 0}
            onChange={(v) => setFactureSel(factures.find(f => f.factureTrackingId === v))}
            optionFilterProp="children">
            {factures.map(f => (
              <Option key={f.factureTrackingId} value={f.factureTrackingId}>
                {f.numeroFacture} - {new Intl.NumberFormat(localeIntl()).format(f.montantTotal)} FCFA
              </Option>
            ))}
          </Select>
        </Form.Item>

        {factureSel && (
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message={t('modales.cheque.facture', { numero: factureSel.numeroFacture })}
            description={t('modales.cheque.montantAttendu', { montant: new Intl.NumberFormat(localeIntl()).format(factureSel.montantTotal) })} />
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="numeroCheque" label={t('modales.cheque.numero')} rules={[{ required: true }]}>
              <Input placeholder={t('modales.cheque.numeroPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="montant" label={t('modales.cheque.montant')} rules={[{ required: true }]}>
              <CBInputNumber min={0} step={1000} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="banqueEmettrice" label={t('modales.cheque.banque')}
          extra={banques.length === 0
            ? t('modales.cheque.aucuneBanqueAide')
            : null}>
          <Select showSearch allowClear
            placeholder={banques.length === 0 ? t('modales.cheque.aucuneBanque') : t('modales.cheque.selectionnerBanque')}
            disabled={banques.length === 0}
            optionFilterProp="children"
            filterOption={(input, opt) => String(opt.children).toLowerCase().includes(input.toLowerCase())}>
            {banques.map(b => (
              <Option key={b.banqueTrackingId} value={b.nom}>
                {b.nom}{b.code ? ` (${b.code})` : ''}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="dateEmission" label={t('modales.cheque.dateEmission')} rules={[{ required: true, message: t('modales.cheque.dateEmissionRequise') }]}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="dateReception" label={t('modales.cheque.dateReception')}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
          <Button onClick={() => { form.resetFields(); setFactureSel(null); onClose?.(); }}>{t('commun.annuler')}</Button>
          <Button type="primary" htmlType="submit" loading={submitting}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
            {t('modales.cheque.enregistrer')}
          </Button>
        </div>
      </Form>
    </CBModal>
  );
}
